/**
 * Money, quantities and rates are DECIMAL STRINGS end to end (API contract, ADR-0007).
 *
 * Nothing in this module turns them into JS numbers:
 * - input parsing normalizes what the user typed (Arabic-Indic digits, separators) into the
 *   API's decimal-string format, without arithmetic;
 * - display rounding works on the digit string (BigInt only for the carry);
 * - Intl.NumberFormat only ever formats integers given as BigInt (exact at any size), and the
 *   result is assembled into the locale's own pattern (sign, currency, percent placement).
 * Business arithmetic (totals, taxes) is the server's job: the editor shows the preview result.
 */

/** Exactly what the API accepts for non-negative amounts, quantities and rates. */
export const API_DECIMAL = /^\d{1,15}(\.\d{1,5})?$/;
const DECIMAL_PARTS = /^(-?)(\d+)(?:\.(\d+))?$/;

const ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
const EXTENDED_ARABIC_INDIC = '۰۱۲۳۴۵۶۷۸۹';

/** Replaces Arabic-Indic and Extended Arabic-Indic digits with ASCII digits. */
export function toAsciiDigits(text: string): string {
  return text.replace(/[٠-٩۰-۹]/g, (digit) => {
    const index = ARABIC_INDIC.indexOf(digit);
    return String(index >= 0 ? index : EXTENDED_ARABIC_INDIC.indexOf(digit));
  });
}

export type DecimalProblem = 'invalid' | 'too_many_decimals' | 'too_large';
export type DecimalInput =
  | { kind: 'empty' }
  | { kind: 'valid'; value: string }
  | { kind: 'invalid'; problem: DecimalProblem };

/**
 * Turns user input into an API decimal string. Accepts Arabic digits, the Arabic decimal separator
 * (٫) and grouping separators; never rounds (too many decimals is reported, not silently cut).
 */
export function parseDecimalInput(raw: string, maxFractionDigits = 5): DecimalInput {
  const text = toAsciiDigits(raw)
    .trim()
    .replace(/\u066b/g, '.')
    .replace(/[\s,\u066c\u00a0\u202f\u2009']/g, '');
  if (text === '') return { kind: 'empty' };
  const match = /^(\d*)(?:\.(\d*))?$/.exec(text);
  const integerDigits = match?.[1] ?? '';
  const fraction = match?.[2] ?? '';
  if (!match || (integerDigits === '' && fraction === ''))
    return { kind: 'invalid', problem: 'invalid' };
  if (fraction.length > maxFractionDigits) return { kind: 'invalid', problem: 'too_many_decimals' };
  const integer = integerDigits.replace(/^0+(?=\d)/, '') || '0';
  if (integer.length > 15) return { kind: 'invalid', problem: 'too_large' };
  return { kind: 'valid', value: fraction ? `${integer}.${fraction}` : integer };
}

/** "100.00000" → "100", "12.50000" → "12.5" (for pre-filling inputs with server values). */
export function trimDecimal(value: string): string {
  const match = DECIMAL_PARTS.exec(value);
  if (!match) return value;
  const fraction = (match[3] ?? '').replace(/0+$/, '');
  return `${match[1] ?? ''}${match[2] ?? '0'}${fraction ? `.${fraction}` : ''}`;
}

/** True for "0", "0.00000", "-0.0" (string test, no arithmetic). */
export function isZeroDecimal(value: string): boolean {
  return /^-?0+(\.0+)?$/.test(value);
}

function assemble(negative: boolean, integer: string, fraction: string): string {
  const int = integer.replace(/^0+(?=\d)/, '') || '0';
  const zero = /^0*$/.test(int + fraction);
  return `${negative && !zero ? '-' : ''}${int}${fraction ? `.${fraction}` : ''}`;
}

/**
 * Rounds a decimal string to `scale` fraction digits, half away from zero (the display rounding
 * Intl uses by default). Works on digits; the carry uses BigInt, so no binary floating point.
 */
export function roundDecimal(value: string, scale: number): string {
  const match = DECIMAL_PARTS.exec(value);
  if (!match) throw new TypeError(`Not a decimal string: ${JSON.stringify(value)}`);
  const negative = match[1] === '-';
  const integer = match[2] ?? '0';
  const fraction = match[3] ?? '';
  if (fraction.length <= scale) return assemble(negative, integer, fraction);
  const roundUp = (fraction[scale] ?? '0') >= '5';
  let digits = BigInt(integer + fraction.slice(0, scale));
  if (roundUp) digits += 1n;
  const text = digits.toString().padStart(scale + 1, '0');
  return assemble(
    negative,
    text.slice(0, text.length - scale),
    scale > 0 ? text.slice(text.length - scale) : '',
  );
}

/**
 * The exact sum of non-negative or negative decimal strings (display aggregate of server values,
 * e.g. the batch review's «إجمالي ما سيُرسل بالجنيه»). Digits and BigInt only; the result keeps the
 * largest scale among the inputs.
 */
export function sumDecimals(values: readonly string[]): string {
  const parts = values.map((value) => {
    const match = DECIMAL_PARTS.exec(value);
    if (!match) throw new TypeError(`Not a decimal string: ${JSON.stringify(value)}`);
    return { negative: match[1] === '-', integer: match[2] ?? '0', fraction: match[3] ?? '' };
  });
  const scale = parts.reduce((n, p) => Math.max(n, p.fraction.length), 0);
  let total = 0n;
  for (const p of parts) {
    const digits = BigInt(p.integer + p.fraction.padEnd(scale, '0'));
    total += p.negative ? -digits : digits;
  }
  const negative = total < 0n;
  const text = (negative ? -total : total).toString().padStart(scale + 1, '0');
  return assemble(
    negative,
    text.slice(0, text.length - scale),
    scale > 0 ? text.slice(text.length - scale) : '',
  );
}

export interface DecimalFormatOptions {
  /** Default 0. */
  minimumFractionDigits?: number;
  /** Default 5 (the ETA scale): server values are shown without losing digits. */
  maximumFractionDigits?: number;
  style?: 'decimal' | 'currency' | 'percent';
  currency?: string;
  currencyDisplay?: 'code' | 'symbol' | 'narrowSymbol' | 'name';
}

const formatterCache = new Map<string, Intl.NumberFormat>();
function formatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let cached = formatterCache.get(key);
  if (!cached) {
    cached = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, cached);
  }
  return cached;
}

interface LocaleSymbols {
  decimal: string;
  digits: string[];
}
const symbolCache = new Map<string, LocaleSymbols>();
function localeSymbols(locale: string): LocaleSymbols {
  let cached = symbolCache.get(locale);
  if (!cached) {
    const parts = formatter(locale, { minimumFractionDigits: 1 }).formatToParts(0n);
    const plain = formatter(locale, { useGrouping: false });
    cached = {
      decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
      digits: Array.from({ length: 10 }, (_, i) => plain.format(BigInt(i))),
    };
    symbolCache.set(locale, cached);
  }
  return cached;
}

const NUMERIC_PARTS = new Set(['integer', 'group', 'decimal', 'fraction']);

/**
 * Formats a decimal string for display in the given locale, exactly (no float conversion).
 * Values that are not decimal strings are returned unchanged rather than guessed at.
 */
export function formatDecimal(
  value: string,
  locale: string,
  options: DecimalFormatOptions = {},
): string {
  if (!DECIMAL_PARTS.test(value)) return value;
  const minimum = options.minimumFractionDigits ?? 0;
  const maximum = Math.max(options.maximumFractionDigits ?? 5, minimum);
  const rounded = roundDecimal(value, maximum);
  const match = DECIMAL_PARTS.exec(rounded);
  if (!match) return value;
  const negative = match[1] === '-';
  let fraction = (match[3] ?? '').replace(/0+$/, '');
  if (fraction.length < minimum) fraction = fraction.padEnd(minimum, '0');

  const symbols = localeSymbols(locale);
  const integerText = formatter(locale, { useGrouping: true }).format(BigInt(match[2] ?? '0'));
  const fractionText = Array.from(fraction, (d) => symbols.digits[d.charCodeAt(0) - 48] ?? d).join(
    '',
  );
  const numberText = fraction ? `${integerText}${symbols.decimal}${fractionText}` : integerText;

  // The locale's pattern (sign, currency and percent placement, bidi marks) from a 1 / -1 template.
  const style = options.style ?? (options.currency ? 'currency' : 'decimal');
  const template = formatter(locale, {
    style,
    ...(style === 'currency'
      ? { currency: options.currency ?? 'EGP', currencyDisplay: options.currencyDisplay ?? 'code' }
      : {}),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(negative ? -1n : 1n);

  let out = '';
  let inserted = false;
  for (const part of template) {
    if (NUMERIC_PARTS.has(part.type)) {
      if (!inserted) out += numberText;
      inserted = true;
    } else {
      out += part.value;
    }
  }
  return out;
}

/** ⌈a / b⌉ for non-negative integers, in integer arithmetic (BigInt). */
export function ceilDiv(a: number, b: number): number {
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b) || a < 0 || b <= 0)
    throw new RangeError('ceilDiv expects a non-negative integer and a positive integer');
  return Number((BigInt(a) + BigInt(b) - 1n) / BigInt(b));
}

/** Number of pages for server pagination (at least 1). */
export function pageCount(total: number, limit: number): number {
  if (total <= 0 || limit <= 0) return 1;
  return ceilDiv(total, limit);
}
